import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Theme } from '@material-ui/core';
import {
    Stepper,
    Step,
    StepButton,
    StepLabel,
    useMediaQuery,
} from '@material-ui/core';
import classNames from 'classnames';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { Block } from '@klp/design/Block';
import { Typography } from '@klp/design/Typography';
import { Loader } from '@klp/design/Loader';
import { REGISTRERING_STATUS } from '@klp/formgenerator/interfaces';
import { getProjectTypeKey } from '@klp/prosjekt/utils';
import { useProsjektSammendragContext } from './context/ProsjektSammendragProvider';
import {
    useRegistreringStatusMapping,
    useSubtypeMapping,
} from '@klp/prosjekt/hooks';
import { useEqualHeightByClass } from '@forvalter/utils/visual/useEqualHeightByClass';
import type { GodkjenningProsessResponse } from '@klp/prosjekt/interfaces';
import {
    formatDateWithTime,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';

const useStyles = (numberOfElements: number) => (theme: Theme) => ({
    stepperContainer: {
        width: '100%',
        maxWidth: `calc(${theme.spacing(30)}px * ${numberOfElements})`,
    },

    stepLabel: {
        width: '100%',
        '& .MuiStepLabel-label': {
            border: `1px solid ${theme.palette.primary.light}`,
            borderRadius: theme.shape.borderRadius,
            '&.MuiStepLabel-active': {
                borderColor: theme.palette.secondary.dark,
                '& .SecondRow': {
                    backgroundColor: theme.palette.secondary.veryLight,
                },
            },
            '& > *': {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
            },
        },
    },
    rejectedStepperButton: {
        '& .MuiStepIcon-completed': {
            color: 'red',
        },
        '& .MuiStepIcon-active': {
            color: 'red',
        },
    },
    firstRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `clamp(0.85em, calc(0.85em + 0.1vw), 1.2em)`,
        lineHeight: 1.1,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    secondRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(0.5),
        fontStyle: 'italic',
        fontSize: `clamp(0.85em, calc(0.85em + 0.1vw), 1.2em)`,
        lineHeight: 1.1,
        backgroundColor: theme.palette.primary?.veryLight,
    },
    thirdRow: {
        color: theme.palette.primary.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `clamp(0.85em, calc(0.85em + 0.1vw), 1.2em)`,
        lineHeight: 1.1,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
});

export const ProsjektSammendragStepper: React.FC = () => {
    const { t } = useTranslation();
    const isMediumScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm'),
    );
    const {
        currentProcess,
        currentProcessIsLoading,
        selectedProcess,
        setSelectedProcessId,
    } = useProsjektSammendragContext();

    const { handleMapping: mapSubtype } = useSubtypeMapping();
    const { handleMapping: mapStatus } = useRegistreringStatusMapping();

    const totalProcesses = [
        ...(currentProcess?.historiskeProsessSteg || []),
        currentProcess,
    ].filter(Boolean) as Array<GodkjenningProsessResponse>;

    const sortedProcesses = totalProcesses.sort((a, b) => {
        const dateA = a.startDato ? new Date(a.startDato).getTime() : 0;
        const dateB = b.startDato ? new Date(b.startDato).getTime() : 0;
        return dateA - dateB;
    });
    const classes = useKlpStyles({}, useStyles(sortedProcesses.length));

    const handleSelectProcess = (prosessInstansId: string) => () => {
        setSelectedProcessId(prosessInstansId);
    };

    useEqualHeightByClass(classes.firstRow, isMediumScreen);
    useEqualHeightByClass(classes.secondRow, isMediumScreen);
    useEqualHeightByClass(classes.thirdRow, isMediumScreen);
    useEqualHeightByClass(classes.stepLabel, isMediumScreen);

    return (
        <Loader isLoading={currentProcessIsLoading}>
            <Block paddingTop="bulky" className={classes.stepperContainer}>
                <Stepper
                    nonLinear={true}
                    orientation="horizontal"
                    alternativeLabel={true}
                >
                    {sortedProcesses.map((prosessInstans) => {
                        const isSelected =
                            selectedProcess?.prosessInstansId ===
                            prosessInstans.prosessInstansId;

                        const isRejected =
                            prosessInstans.registreringStatus ===
                            REGISTRERING_STATUS.AVVIST;

                        return (
                            <Step key={prosessInstans.prosessInstansId}>
                                <StepButton
                                    className={classNames({
                                        [classes.rejectedStepperButton]:
                                            isRejected,
                                    })}
                                    completed={!!prosessInstans.fullfortDato}
                                    active={isSelected}
                                    onClick={handleSelectProcess(
                                        prosessInstans.prosessInstansId,
                                    )}
                                >
                                    <StepLabel className={classes.stepLabel}>
                                        <Block className={classes.firstRow}>
                                            {t(
                                                `${t(getProjectTypeKey(prosessInstans.type))} - ${mapSubtype(
                                                    prosessInstans.stegNavn,
                                                )}`,
                                            )}
                                        </Block>
                                        <Block
                                            className={classNames(
                                                'SecondRow',
                                                classes.secondRow,
                                            )}
                                        >
                                            {mapStatus(
                                                prosessInstans.registreringStatusNavn,
                                            )}
                                        </Block>
                                        <Block className={classes.thirdRow}>
                                            {prosessInstans.fullfortDato && (
                                                <Typography
                                                    translation="project.completedDate"
                                                    translationOptions={{
                                                        date: formatDateWithTime(
                                                            parseSafeDate(
                                                                prosessInstans.fullfortDato,
                                                            ),
                                                        ),
                                                    }}
                                                    klpTypographyVariant="small"
                                                />
                                            )}
                                        </Block>
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        );
                    })}
                </Stepper>
            </Block>
        </Loader>
    );
};
